"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";

const tabIds = ["mission", "vision", "value"] as const;

export default function AboutStorySection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"mission" | "vision" | "value">("mission");

  return (
    <section className="overflow-hidden border-b border-border-soft bg-white py-12 sm:py-16 md:py-24">
      <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 sm:gap-10 md:grid-cols-[0.9fr_1.1fr] md:items-start md:gap-12 md:px-6">
        {/* Left - Image */}
        <div className="relative order-2 md:order-1">
          <div className="relative h-[320px] overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/50 md:h-[400px]">
            <Image
              src="/gallary/9.jpeg"
              alt={t("images.aboutStory")}
              fill
              sizes="(max-width: 768px) 100vw, 520px"
              className="object-cover"
              loading="lazy"
              quality={75}
            />
          </div>
          {/* Decorative circle */}
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary-soft/60 md:-right-12 md:-top-12 md:h-40 md:w-40" />
        </div>

        {/* Right - Content with tabs */}
        <div className="order-1 space-y-6 md:order-2">
          <span className="inline-block rounded-full bg-primary-soft px-4 py-1.5 text-sm font-semibold text-primary">
            {t("about.committedToSport")}
          </span>
          <h2 className="text-2xl font-bold uppercase tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
            {t("about.ourStory")}
          </h2>

          {/* Tab buttons */}
          <div className="flex flex-wrap gap-2">
            {tabIds.map((id) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                  activeTab === id
                    ? "bg-primary-soft text-primary"
                    : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                {t(`about.our${id.charAt(0).toUpperCase() + id.slice(1)}`)}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="space-y-6 pt-2">
            <div>
              <h3 className="text-xl font-bold text-slate-900">{t(`about.${activeTab}Heading`)}</h3>
              <p className="mt-3 leading-relaxed text-slate-600">{t(`about.${activeTab}Content`)}</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{t(`about.${activeTab}Subheading`)}</h3>
              <p className="mt-2 leading-relaxed text-slate-600">{t(`about.${activeTab}Subcontent`)}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
